import { makeAutoObservable, runInAction } from "mobx";

class MediaManager {
  // Singleton instance
  static instance = null;

  constructor() {
    if (MediaManager.instance) {
      return MediaManager.instance;
    }
    MediaManager.instance = this;

    /* MEDIA PLAYER */
    this.showingMediaPlayer = false;
    this.messageToView = null;
    this.serieToView = null;
    this.showingFullScreenVideo = false;
    this.videoStartTime = 0;
    this.videoMuted = true;

    makeAutoObservable(this);
  }

  // Singleton access method
  static getInstance() {
    if (!MediaManager.instance) {
      MediaManager.instance = new MediaManager();
    }
    return MediaManager.instance;
  }

  setProperty(propertyName, value) {
    if (propertyName in this) {
      this[propertyName] = value;
    } else {
      console.warn(
        `Property "${propertyName}" does not exist on MediaManager.`
      );
    }
  }

  // // Load image from the picker
  async loadImage() {
    return new Promise((resolve, reject) => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.style.display = "none";

      input.onchange = () => {
        const file = input.files?.[0];
        if (file) {
          const reader = new FileReader();
          reader.onload = () => {
            resolve({
              uri: reader.result,
              file,
              cancelled: false,
            });
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        } else {
          resolve({ cancelled: true });
        }
      };

      document.body.appendChild(input);
      input.click();
      document.body.removeChild(input);
    });
  }

  async compressMediaFileWeb(mediaURL, id, mediaType) {
    if (mediaType !== "image") {
      console.error("Only image compression is supported");
      return null;
    }

    // target ~300 KB
    const TARGET_SIZE = 300 * 1024;

    try {
      // 1) fetch the image as a Blob
      const resp = await fetch(mediaURL);
      let blob = await resp.blob();

      // 2) if it's too big, re‑encode via canvas
      if (blob.size > TARGET_SIZE) {
        blob = await this.compressBlobWithCanvas(blob, TARGET_SIZE);
      }

      // 3) turn the Blob back into a File (so you can upload it)
      const file = new File([blob], id + ".jpg", { type: "image/jpeg" });

      // 4) create an object URL for preview
      const uri = URL.createObjectURL(blob);

      return { uri, file };
    } catch (e) {
      console.error("compression failed:", e);
      return null;
    }
  }

  async compressBlobWithCanvas(blob, targetSize) {
    // load into an <img>
    const img = await new Promise((res, rej) => {
      const url = URL.createObjectURL(blob);
      const image = new Image();
      image.onload = () => {
        URL.revokeObjectURL(url);
        res(image);
      };
      image.onerror = rej;
      image.src = url;
    });

    // paint on off‑screen canvas
    const canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);

    // estimate quality factor
    let quality = targetSize / blob.size;
    quality = Math.max(Math.min(quality, 1), 0.1);

    // export back out
    return await new Promise((res) => {
      canvas.toBlob((newBlob) => res(newBlob), "image/jpeg", quality);
    });
  }

  showMediaPlayer(message, serie) {
    if (serie) {
      this.setProperty("serieToView", serie);
      this.setProperty("messageToView", null);
      const client = serie?.exercise?.workout?.block?.client;
      const coach = client?.coach;
      if (client && coach) {
        coach.setProperty("selectedChatId", client.id);
        coach.setProperty("showingChatList", true);
      }
    }
    if (message) {
      this.setProperty("messageToView", message);
      this.setProperty("serieToView", null);
    }

    this.setProperty("showingMediaPlayer", true);
  }

  closeMediaPlayer() {
    this.setProperty("showingMediaPlayer", false);
    this.setProperty("messageToView", null);
    this.setProperty("serieToView", null);
    this.setProperty("showingFullScreenVideo", false);
    this.setProperty("videoStartTime", 0);
  }

  toggleFullScreenVideo() {
    this.setProperty("showingFullScreenVideo", !this.showingFullScreenVideo);
  }

  openFullScreenVideo(startTime = 0) {
    this.setProperty("videoStartTime", startTime);
    this.setProperty("showingFullScreenVideo", true);
  }

  closeFullScreenVideo() {
    this.setProperty("showingFullScreenVideo", false);
    this.setProperty("videoStartTime", 0);
  }

  setVideoMuted(muted) {
    this.setProperty("videoMuted", muted);
  }
}

export default MediaManager;
